import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthState } from "@/hooks/useAuth"
import { useUserData } from "@/hooks/useUserData"
import { useFlashDetail } from "@/hooks/useFlashDetail"
import { useAnalytics } from "@/hooks/useAnalytics"
import { fetchArtistById } from "@/lib/tattooData"
import type { CachedChatDisplayData } from "@/lib/chatListCache"
import {
  createSimpleChat,
  fetchChatById,
  markMessagesAsRead,
  fetchUserById,
  trackChatOpened,
  type ChatUserInfo,
  addImageUrlMessageToChat,
  addMessageToChat,
  addCombinedMessageToChat,
  listenToChatUpdates,
  addWelcomeMessageToChat,
  setTypingIndicator,
  clearTypingIndicator,
  type SenderType,
  CHAT_COMBINED_ATTACHMENT_LIMIT,
  buildMessageReplyToSnapshot,
  buildBurstImageReplySnapshot,
} from "@/lib/chatData"
import { updateMatch } from "@/lib/leadData"
import { sendMetaStandardEvent } from "@/lib/metaEvents"
import type { Artist, Chat, ChatSettings, Message, MessageReplyTo, PendingImage, PortfolioItem } from "@/types/models"
import { doc as fsDoc, getDoc, onSnapshot, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { messageQueue, createPendingMessage, markForRetry, type PendingMessage } from '@/lib/messageQueue'
/**
 * PHASE 1 OPTIMISTIC MESSAGING IMPROVEMENTS (Applied)
 * 
 * 1. Fixed Message Fingerprinting:
 *    - Use clientGeneratedId as primary match (most reliable)
 *    - Simplified fallback fingerprint without imageUrl (prevents blob URL mismatch)
 * 
 * 2. Queue All Messages Immediately:
 *    - Text messages queued before send
 *    - Image messages queued with blob URLs, then updated after upload
 *    - No messages lost if upload fails
 * 
 * 3. Simplified Status Tracking:
 *    - Clear status transitions: 'sending' -> 'sent' -> 'failed'
 *    - Removed blob URL checks that prevented confirmation
 *    - Enhanced logging for debugging
 * 
 * Next Phase: Upload images BEFORE creating optimistic messages for cleaner flow
 */

export function useChatDetail(cachedDisplayData?: CachedChatDisplayData | null) {
  const { chatId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuthState()
  const { userData } = useUserData()
  const { trackEvent } = useAnalytics()

  // State
  const [artist, setArtist] = useState<Artist | null>(null)
  const [chatUser, setChatUser] = useState<ChatUserInfo | null>(null)
  const [otherPartyLoading, setOtherPartyLoading] = useState(true)
  const [chat, setChat] = useState<Chat | null>(null)
  const [chatLoading, setChatLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isNewChatCreated, setIsNewChatCreated] = useState(false)
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([])
  const [attachedArtistPortfolioItems, setAttachedArtistPortfolioItems] = useState<PortfolioItem[]>([])
  const [isArtist, setIsArtist] = useState(false)
  const [artistChatSettings, setArtistChatSettings] = useState<ChatSettings | null>(null)
  const [artistSubscriptionStatus, setArtistSubscriptionStatus] = useState<string | null>(null)
  const [userMadeDecisionOnArtistContact, setUserMadeDecisionOnArtistContact] = useState(false)
  const [otherPartyIsTyping, setOtherPartyIsTyping] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [messageReplyDraft, setMessageReplyDraft] = useState<MessageReplyTo | null>(null)
  
  // Track the created chat ID for in-place creation (without navigation)
  // When a new chat is created from /chats/new, we update browser history silently
  // but keep this ID to avoid resetting state during the history update
  const [createdChatId, setCreatedChatId] = useState<string | null>(null)

  // Refs
  const initialMessagesSentRef = useRef(false)
  const otherPartyLoadedRef = useRef(false)
  const cachedDataAppliedRef = useRef(false)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const markAsReadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Track optimistic message IDs that haven't been confirmed by Firestore yet
  const optimisticMessageIdsRef = useRef<Set<string>>(new Set())
  const trackedChatOpenRef = useRef<Set<string>>(new Set())
  const didHydrateFlashIntoPortfolioAttachmentsRef = useRef(false)

  // URL params
  const artistId = searchParams.get('artistId')
  const flashId = searchParams.get('flashId')
  const matchIdParam = searchParams.get('matchId')
  const matcherSessionId = searchParams.get('matcherSessionId')
  const matcherUserId = searchParams.get('matcherUserId')
  const matcherSelectedCity = searchParams.get('matcherSelectedCity') || ''
  const matcherIntentMode = searchParams.get('matcherIntentMode') === 'specific' ? 'specific' : 'exploring'
  const matcherIdeaText = searchParams.get('matcherIdeaText') || ''
  
  // Flash details
  const { flash } = useFlashDetail(flashId || undefined)

  useEffect(() => {
    if (!flashId) {
      didHydrateFlashIntoPortfolioAttachmentsRef.current = false
    }
  }, [flashId])

  useEffect(() => {
    if (!flashId || !flash || didHydrateFlashIntoPortfolioAttachmentsRef.current) return
    didHydrateFlashIntoPortfolioAttachmentsRef.current = true
    setAttachedArtistPortfolioItems(previousItems =>
      previousItems.some(item => item.id === flash.id) ? previousItems : [...previousItems, flash]
    )
  }, [flashId, flash])

  useEffect(() => {
    const maxPortfolioAttachmentSlots = Math.max(
      0,
      CHAT_COMBINED_ATTACHMENT_LIMIT - pendingImages.length
    )
    setAttachedArtistPortfolioItems(previousAttachments =>
      previousAttachments.length <= maxPortfolioAttachmentSlots
        ? previousAttachments
        : previousAttachments.slice(0, maxPortfolioAttachmentSlots)
    )
  }, [pendingImages.length])

  // Computed values
  // Use createdChatId (from in-place creation) or chatId from URL
  const effectiveChatId = createdChatId || (chatId !== 'new' ? chatId : null)
  // Only truly "new" if URL says 'new' AND we haven't created a chat yet
  const isNewChat = chatId === 'new' && !createdChatId

  // Effect to update URL when createdChatId is set
  // Use replaceState to update browser history silently (no React Router re-render)
  // This allows seamless transition from /chats/new to /chats/{id} during creation
  useEffect(() => {
    if (createdChatId) {
      // Update browser history for bookmarking/refresh, but don't trigger React Router
      window.history.replaceState(null, '', `/chats/${createdChatId}`)
      console.log('[ChatDetail] Updated browser history to:', `/chats/${createdChatId}`)
    }
  }, [createdChatId])

  // Helper functions
  const getClientDisplayName = (client: ChatUserInfo | null): string => {
    if (!client) return 'User'
    if (client.instagram) {
      return client.instagram.startsWith('@') ? client.instagram.substring(1) : client.instagram
    }
    if (client.displayName) return client.displayName
    if (client.email) return client.email
    return 'User'
  }

  const getOtherPartyDisplayName = () => {
    if (isArtist) {
      return getClientDisplayName(chatUser)
    } else {
      if (!artist) return t('common.artistName')
      return artist.displayName || t('common.artistName')
    }
  }

  const getOtherPartySubtitle = () => {
    if (isArtist) {
      return ''
    } else {
      // For user view, don't show any subtitle
      return ''
    }
  }

  const beginReplyToMessage = useCallback((targetMessage: Message) => {
    const snapshot = buildMessageReplyToSnapshot(targetMessage)
    if (snapshot) {
      setMessageReplyDraft(snapshot)
    }
  }, [])

  const beginReplyToImageBurst = useCallback((burstMessages: Message[]) => {
    const snapshot = buildBurstImageReplySnapshot(burstMessages)
    if (snapshot) {
      setMessageReplyDraft(snapshot)
    }
  }, [])

  const clearMessageReplyDraft = useCallback(() => {
    setMessageReplyDraft(null)
  }, [])

  const getReplyAuthorDisplayLabel = useCallback(
    (reply: MessageReplyTo) => {
      if (!user) return '…'
      if (reply.senderId === user.uid) {
        return t('chat.replyYou')
      }
      if (reply.senderType === 'artist') {
        return artist?.displayName || t('common.artistName')
      }
      if (chatUser?.instagram) {
        const rawHandle = chatUser.instagram.startsWith('@')
          ? chatUser.instagram.slice(1)
          : chatUser.instagram
        return `@${rawHandle}`
      }
      return chatUser?.displayName || chatUser?.email || t('chat.replyUnknownParticipant')
    },
    [user, artist, chatUser, t]
  )



  /**
   * Retry sending a pending message from the queue
   * Returns true if successful, false if failed
   */
  const retryPendingMessage = async (pendingMsg: PendingMessage): Promise<boolean> => {
    console.log('[Retry] Attempting to send message:', {
      clientGeneratedId: pendingMsg.clientGeneratedId,
      attempt: pendingMsg.attempts + 1,
      type: pendingMsg.type
    })

    try {
      // Try to send the message to Firestore
      if (pendingMsg.type === 'image') {
        await addImageUrlMessageToChat(
          pendingMsg.chatId,
          pendingMsg.imageUrl!,
          pendingMsg.senderId,
          pendingMsg.senderType,
          pendingMsg.content,
          pendingMsg.clientGeneratedId,
          pendingMsg.metadata,
          pendingMsg.replyTo
        )
      } else {
        await addMessageToChat(
          pendingMsg.chatId,
          pendingMsg.content,
          pendingMsg.senderId,
          pendingMsg.senderType,
          'text',
          pendingMsg.clientGeneratedId,
          pendingMsg.replyTo
        )
      }

      console.log('[Retry] Message sent successfully:', pendingMsg.clientGeneratedId)
      
      // Remove from queue on success
      await messageQueue.dequeue(pendingMsg.clientGeneratedId)
      
      // PHASE 1 FIX: Update message status to 'sent' in chat state
      setChat(prevChat => {
        if (!prevChat) return prevChat
        
        return {
          ...prevChat,
          messages: prevChat.messages.map(m =>
            m.clientGeneratedId === pendingMsg.clientGeneratedId
              ? { ...m, status: 'sent' as const, sentAt: new Date(), sendAttempts: pendingMsg.attempts + 1 }
              : m
          )
        }
      })
      
      return true
    } catch (error) {
      console.error('[Retry] Failed to send message:', error)
      
      // Update pending message with failure info
      const updatedMsg = markForRetry(pendingMsg, String(error))
      
      // Check if we've exceeded max retries
      if (messageQueue.hasExceededMaxRetries(updatedMsg.attempts)) {
        console.error('[Retry] Max retries exceeded for message:', pendingMsg.clientGeneratedId)
        
        // Update message status to failed in chat state
        setChat(prevChat => {
          if (!prevChat) return prevChat
          
          return {
            ...prevChat,
            messages: prevChat.messages.map(m =>
              m.clientGeneratedId === pendingMsg.clientGeneratedId
                ? { 
                    ...m, 
                    status: 'failed' as const, 
                    failedAt: new Date(), 
                    lastSendError: String(error),
                    sendAttempts: updatedMsg.attempts 
                  }
                : m
            )
          }
        })
      } else {
        // Update queue with new retry time
        await messageQueue.update(updatedMsg)
        
        // Update message with attempt count
        setChat(prevChat => {
          if (!prevChat) return prevChat
          
          return {
            ...prevChat,
            messages: prevChat.messages.map(m =>
              m.clientGeneratedId === pendingMsg.clientGeneratedId
                ? { ...m, sendAttempts: updatedMsg.attempts, lastSendError: String(error) }
                : m
            )
          }
        })
      }
      
      return false
    }
  }

  /**
   * Process all messages ready for retry from the queue
   */
  const processMessageQueue = async () => {
    try {
      const readyMessages = await messageQueue.getReadyForRetry()
      
      if (readyMessages.length > 0) {
        console.log('[Queue] Processing', readyMessages.length, 'messages ready for retry')
        
        // Process messages sequentially to maintain order
        for (const msg of readyMessages) {
          await retryPendingMessage(msg)
        }
      }
    } catch (error) {
      console.error('[Queue] Error processing message queue:', error)
    }
  }

  // Message queue initialization and polling

  // Effect 0.5: Initialize message queue and set up retry polling
  useEffect(() => {
    let retryInterval: NodeJS.Timeout | null = null
    
    const initQueue = async () => {
      try {
        // Initialize IndexedDB
        await messageQueue.init()
        console.log('[MessageQueue] Initialized successfully')
        
        // Process any pending messages immediately on mount
        await processMessageQueue()
        
        // Set up polling for retries every 5 seconds
        retryInterval = setInterval(() => {
          processMessageQueue().catch(err => 
            console.error('[MessageQueue] Error in retry polling:', err)
          )
        }, 5000)
      } catch (error) {
        console.error('[MessageQueue] Failed to initialize:', error)
      }
    }
    
    initQueue()
    
    return () => {
      if (retryInterval) {
        clearInterval(retryInterval)
      }
    }
  }, [])

  // Effect 1: Reset state when chatId changes
  useEffect(() => {
    // Don't reset if we just created the chat in-place (createdChatId is set)
    // This preserves artist/user data already loaded for new chats
    if (createdChatId) return
    
    // Reset refs and state when switching to a different chat
    otherPartyLoadedRef.current = false
    cachedDataAppliedRef.current = false
    setArtist(null)
    setChatUser(null)
    setUserMadeDecisionOnArtistContact(false)
    setMessageReplyDraft(null)
  }, [chatId, createdChatId])

  // Effect 1.5: Apply cached display data for instant header rendering
  // This allows the header to show avatar and name immediately without waiting for Firestore fetch
  useEffect(() => {
    console.log('[ChatDetail] Effect 1.5 - Cache check:', {
      hasCachedData: !!cachedDisplayData,
      alreadyApplied: cachedDataAppliedRef.current,
      otherPartyLoaded: otherPartyLoadedRef.current
    })
    
    // Only apply cache once when the component mounts with cached data
    if (!cachedDisplayData || cachedDataAppliedRef.current) return
    
    // The cached data structure tells us which view it came from:
    // - If userId is present, this was cached from artist view
    // - If artistId is present, this was cached from user view
    // We can trust this and apply it immediately for instant rendering!
    
    console.log('[ChatDetail] Applying cached display data:', {
      displayName: cachedDisplayData.displayName,
      hasAvatar: !!cachedDisplayData.avatarUrl,
      hasUserId: !!cachedDisplayData.userId,
      hasArtistId: !!cachedDisplayData.artistId,
      isArtist
    })
    
    cachedDataAppliedRef.current = true
    
    // Populate artist or chatUser from cache based on current user's role
    if (isArtist && cachedDisplayData.userId) {
      // Artist view - populate chatUser from cache
      setChatUser({
        uid: cachedDisplayData.userId,
        email: cachedDisplayData.email,
        displayName: cachedDisplayData.displaySecondary || cachedDisplayData.displayName,
        instagram: cachedDisplayData.instagram,
        avatarUrl: cachedDisplayData.avatarUrl || null,
      } as ChatUserInfo)
      console.log('[ChatDetail] Populated chatUser from cache (artist view)')
      otherPartyLoadedRef.current = true // Mark as loaded since we successfully populated chatUser
      setOtherPartyLoading(false)
    } else if (!isArtist && cachedDisplayData.artistId) {
      // User view - populate artist from cache
      setArtist({
        id: cachedDisplayData.artistId,
        displayName: cachedDisplayData.displayName,
        firstName: cachedDisplayData.firstName,
        lastName: cachedDisplayData.lastName,
        avatarUrl: cachedDisplayData.avatarUrl || undefined,
      } as Artist)
      console.log('[ChatDetail] Populated artist from cache (user view)')
      otherPartyLoadedRef.current = true // Mark as loaded since we successfully populated artist
      setOtherPartyLoading(false)
    } else {
      console.log('[ChatDetail] Cache has no valid data for current user role, will fetch from Firestore')
      // Don't set otherPartyLoadedRef - let Effect 4 do the fetch
    }
  }, [cachedDisplayData, isArtist])

  // Effect 2: Set user type (artist or regular user)
  useEffect(() => {
    if (!user || !userData) return
    
    const userIsArtist = userData.isArtist === true
    console.log('[ChatDetail] User type:', userIsArtist ? 'Artist' : 'User')
    setIsArtist(userIsArtist)
  }, [user, userData])

  // Effect 2.5: Load artist's chat settings from their bookingSystems document
  useEffect(() => {
    // For new chats, use artistId from URL params
    // For existing chats, use chat.artistId
    const targetArtistId = isNewChat ? artistId : chat?.artistId
    
    if (!targetArtistId) {
      console.log('[ChatDetail] No artist ID available for loading chat settings')
      setArtistChatSettings(null)
      return
    }

    console.log('[ChatDetail] Setting up listener for artist chat settings, artistId:', targetArtistId)
    
    // Listen to the artist's bookingSystems document to get their chat settings
    const unsubscribe = onSnapshot(
      fsDoc(db, 'bookingSystems', targetArtistId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data()
          const settingsData = data?.chatSettings
          const subscriptionData = data?.bookingSubscription
          
          console.log('[ChatDetail] Raw chatSettings from artist bookingSystems:', data?.chatSettings)
          console.log('[ChatDetail] Artist subscription status:', subscriptionData?.status)
          console.log('[ChatDetail] Artist chat settings data:', settingsData)
          
          // Store subscription status
          setArtistSubscriptionStatus(subscriptionData?.status || null)
          
          if (settingsData) {
            // Convert Timestamps to Dates
            const convertedSettings: ChatSettings = {
              mode: settingsData.mode,
              updatedAt: settingsData.updatedAt instanceof Timestamp 
                ? settingsData.updatedAt.toDate() 
                : settingsData.updatedAt instanceof Date 
                  ? settingsData.updatedAt
                  : undefined,
              welcomeMessage: settingsData.welcomeMessage ? {
                content: settingsData.welcomeMessage.content,
                updatedAt: settingsData.welcomeMessage.updatedAt instanceof Timestamp
                  ? settingsData.welcomeMessage.updatedAt.toDate()
                  : settingsData.welcomeMessage.updatedAt instanceof Date
                    ? settingsData.welcomeMessage.updatedAt
                    : undefined
              } : undefined,
              aiAssistant: settingsData.aiAssistant ? {
                enabled: settingsData.aiAssistant.enabled,
                personaPrompt: settingsData.aiAssistant.personaPrompt,
                maxAutoReplies: settingsData.aiAssistant.maxAutoReplies,
                handoverMessage: settingsData.aiAssistant.handoverMessage,
                updatedAt: settingsData.aiAssistant.updatedAt instanceof Timestamp
                  ? settingsData.aiAssistant.updatedAt.toDate()
                  : settingsData.aiAssistant.updatedAt instanceof Date
                    ? settingsData.aiAssistant.updatedAt
                    : undefined
              } : undefined
            }
            
            console.log('[ChatDetail] Artist chat settings loaded, mode:', convertedSettings.mode)
            setArtistChatSettings(convertedSettings)
          } else {
            console.log('[ChatDetail] No chat settings found in artist bookingSystems')
            setArtistChatSettings(null)
          }
        } else {
          console.log('[ChatDetail] Artist bookingSystems document not found')
          setArtistChatSettings(null)
          setArtistSubscriptionStatus(null)
        }
      },
      (error) => {
        console.error('[ChatDetail] Error listening to artist chat settings:', error)
        setArtistChatSettings(null)
        setArtistSubscriptionStatus(null)
      }
    )

    return () => {
      console.log('[ChatDetail] Cleaning up artist chat settings listener')
      unsubscribe()
    }
  }, [isNewChat, artistId, chat?.artistId])


  // Effect 3: Real-time chat listener (ONLY updates chat state)
  useEffect(() => {
    // Don't set up listener if no effective chat ID
    if (!effectiveChatId) {
      setChatLoading(false)
      return
    }

    console.log('[ChatDetail] Setting up real-time listener for:', effectiveChatId)
    // Only set loading if we don't already have chat data (from optimistic update)
    if (!chat) {
      setChatLoading(true)
    }
    
    // Helper to create a fingerprint for matching optimistic messages with Firestore messages
    // PHASE 1 FIX: Use clientGeneratedId as primary match, simplified fingerprint as fallback
    // This avoids issues with blob URLs changing to permanent URLs
    const getMessageFingerprint = (m: { content: string; senderId: string; senderType: string; type: string; imageUrl?: string; timestamp?: Date; id?: string; clientGeneratedId?: string }) => {
      // Primary: Use clientGeneratedId if available (most reliable)
      if (m.clientGeneratedId) {
        return `client:${m.clientGeneratedId}`
      }
      
      // Fallback: Use simplified fingerprint without imageUrl (which changes from blob to permanent)
      const timestampStr = m.timestamp ? m.timestamp.getTime().toString() : m.id || ''
      return `${m.senderId}|${m.senderType}|${m.type}|${timestampStr}`
    }
    
    // Set up real-time listener that updates chat state whenever messages change
    const unsubscribe = listenToChatUpdates(effectiveChatId, async (chatData) => {
      if (chatData) {
        console.log('[ChatDetail] Real-time update - messages count:', chatData.messages?.length || 0)
        
        // Smart merge: preserve optimistic messages that aren't in Firestore yet
        setChat(prevChat => {
          if (!prevChat) return chatData
          
          // If no optimistic messages are pending, accept Firestore data directly
          if (optimisticMessageIdsRef.current.size === 0) {
            return chatData
          }
          
          // Build maps for dual ID matching (clientGeneratedId is primary, fingerprint is fallback)
          const firestoreByClientId = new Map<string, Message>()
          const firestoreFingerprints = new Set<string>()
          
          chatData.messages.forEach(m => {
            if (m.clientGeneratedId) {
              firestoreByClientId.set(m.clientGeneratedId, m)
            }
            firestoreFingerprints.add(getMessageFingerprint(m))
          })
          
          // Check each optimistic message to see if it's now confirmed in Firestore
          for (const id of Array.from(optimisticMessageIdsRef.current)) {
            const msg = prevChat.messages.find(m => m.id === id)
            if (!msg) {
              // Message not found in our state, remove from tracking
              optimisticMessageIdsRef.current.delete(id)
              continue
            }
            
            // Primary match: use clientGeneratedId for reliable matching
            if (msg.clientGeneratedId && firestoreByClientId.has(msg.clientGeneratedId)) {
              const confirmedMsg = firestoreByClientId.get(msg.clientGeneratedId)!
              console.log('[ChatDetail] 📋 Message confirmed via clientGeneratedId:', msg.clientGeneratedId)
              
              // PHASE 1 FIX: Accept confirmation immediately for all message types
              // The permanent URL should already be set before Firestore write
              console.log('[ChatDetail] ✅ Marking as sent:', msg.type, 'URL:', msg.imageUrl?.substring(0, 60))
              
              // Update message status to 'sent' in our local state
              const updatedMsg = { ...msg, status: 'sent' as const, sentAt: confirmedMsg.sentAt || new Date() }
              Object.assign(msg, updatedMsg)
              
              // Remove from optimistic tracking
              optimisticMessageIdsRef.current.delete(id)
              
              // Remove from message queue if present
              messageQueue.dequeue(msg.clientGeneratedId).catch(err => 
                console.error('[ChatDetail] Error removing from queue:', err)
              )
              continue
            }
            
            // Fallback match: use fingerprint (for backwards compatibility)
            const fingerprint = getMessageFingerprint(msg)
            if (firestoreFingerprints.has(fingerprint)) {
              console.log('[ChatDetail] Message confirmed via fingerprint (legacy):', fingerprint)
              
              // PHASE 1 FIX: Accept confirmation immediately for all message types
              console.log('[ChatDetail] ✅ Marking as sent via fingerprint:', msg.type)
              
              // Update message status
              const updatedMsg = { ...msg, status: 'sent' as const, sentAt: new Date() }
              Object.assign(msg, updatedMsg)
              
              // This optimistic message is now confirmed in Firestore
              optimisticMessageIdsRef.current.delete(id)
            }
          }
          
          // If all optimistic messages are now confirmed, we can now safely merge with Firestore data
          if (optimisticMessageIdsRef.current.size === 0) {
            console.log('[ChatDetail] All optimistic messages confirmed, merging with Firestore data')
            
            // Build a map of confirmed messages by clientGeneratedId
            const confirmedByClientId = new Map<string, Message>()
            prevChat.messages.forEach(msg => {
              if (msg.clientGeneratedId && msg.status === 'sent') {
                confirmedByClientId.set(msg.clientGeneratedId, msg)
              }
            })
            
            // Merge: keep confirmed messages from our state, add new Firestore messages
            const mergedMessages = [...prevChat.messages]
            const existingClientIds = new Set(
              prevChat.messages
                .filter(m => m.clientGeneratedId)
                .map(m => m.clientGeneratedId!)
            )
            
            // Add any Firestore messages that aren't already confirmed in our state
            chatData.messages.forEach(fbMsg => {
              if (fbMsg.clientGeneratedId && !existingClientIds.has(fbMsg.clientGeneratedId)) {
                // This is a new message from Firestore that we don't have
                mergedMessages.push(fbMsg)
              }
            })
            
            // Sort by timestamp to maintain order
            // BUGFIX: Stable sort - preserve insertion order when timestamps are equal
            mergedMessages.sort((a, b) => {
              const aTime = (a.timestamp as any)?.toDate?.() || a.timestamp as Date || new Date(0)
              const bTime = (b.timestamp as any)?.toDate?.() || b.timestamp as Date || new Date(0)
              const timeDiff = new Date(aTime).getTime() - new Date(bTime).getTime()
              
              // If timestamps are equal (sent in same batch), preserve array order
              if (timeDiff === 0) {
                return 0 // Maintains insertion order (stable sort)
              }
              return timeDiff
            })
            
            return {
              ...chatData,
              messages: mergedMessages
            }
          }
          
          // Still have pending optimistic messages - keep current message order to avoid jitter
          // Only update other chat metadata from Firestore
          console.log('[ChatDetail] Keeping current messages, pending optimistic:', optimisticMessageIdsRef.current.size)
          return {
            ...chatData,
            messages: prevChat.messages
          }
        })
        setChatLoading(false)
        
        // Update typing indicator based on other party's status
        const otherPartyTyping = isArtist ? chatData.userIsTyping : chatData.artistIsTyping
        setOtherPartyIsTyping(!!otherPartyTyping)
        
        // Debounce mark as read to avoid transaction conflicts
        // Clear any pending mark-as-read call
        if (markAsReadTimeoutRef.current) {
          clearTimeout(markAsReadTimeoutRef.current)
        }
        
        // Mark messages as read after debounce delay
        // This ensures that rapid message sends don't cause transaction conflicts
        markAsReadTimeoutRef.current = setTimeout(async () => {
          try {
            await markMessagesAsRead(effectiveChatId)
          } catch (error) {
            // Error is already logged in markMessagesAsRead as a warning
          }
        }, 500) // Increased delay to reduce contention
      } else {
        setChatLoading(false)
      }
    })
    
    // Cleanup listener on unmount or when effectiveChatId changes
    return () => {
      console.log('[ChatDetail] Cleaning up listener for:', effectiveChatId)
      unsubscribe()
      // Clear any pending mark-as-read timeout
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current)
      }
    }
  }, [effectiveChatId, isArtist])

  // Track chat opened once per chat ID
  useEffect(() => {
    if (!effectiveChatId || !chat || !userData) return
    if (trackedChatOpenRef.current.has(effectiveChatId)) return

    const role = userData.isArtist ? 'artist' : 'user'
    const source = searchParams.get('source') || undefined

    trackEvent('chat_opened', {
      chat_id: effectiveChatId,
      role,
      artist_id: chat.artistId,
      user_id: chat.userId,
      source
    })

    trackedChatOpenRef.current.add(effectiveChatId)
  }, [chat, effectiveChatId, searchParams, trackEvent, userData])

  // Effect 4: Load other party data (artist or user) when chat loads
  useEffect(() => {
    console.log('[ChatDetail] Effect 4 - Load other party check:')
    console.log('  isNewChat:', isNewChat)
    console.log('  artistId:', artistId)
    console.log('  hasChat:', !!chat)
    console.log('  hasUser:', !!user)
    console.log('  hasUserData:', !!userData)
    console.log('  otherPartyLoaded:', otherPartyLoadedRef.current)
    console.log('  createdChatId:', createdChatId)
    console.log('  currentArtist:', artist)
    console.log('  currentChatUser:', chatUser)
    
    const loadOtherPartyData = async () => {
      // For new chats, load artist from URL param (even if unauthenticated)
      if (isNewChat) {
        if (!artistId) {
          setOtherPartyLoading(false)
          return
        }

        setOtherPartyLoading(true)
        try {
          const artistData = await fetchArtistById(artistId)
          setArtist(artistData)
          console.log('[ChatDetail] Loaded artist for new chat:', artistData?.displayName || 'Unknown')
        } catch (error) {
          console.error('[ChatDetail] Error loading artist:', error)
        } finally {
          setOtherPartyLoading(false)
        }
        return
      }

      // If we just created a chat in-place (createdChatId is set), skip loading again
      // The artist/user data is already available from new chat initialization
      if (createdChatId && otherPartyLoadedRef.current) {
        return
      }

      // For existing chats, need user data
      if (!user || !userData) return
      
      // ✅ If we've already loaded from cache, skip fetching fresh data
      // The cached data is fresh enough (from the chat list) and we trust it completely
      if (otherPartyLoadedRef.current) {
        console.log('[ChatDetail] Already loaded from cache, skipping fresh fetch')
        return
      }

      const userIsArtist = userData.isArtist === true

      // For existing chats, wait for chat to load, then load other party
      if (!chat) return

      // If we get here without cache, load fresh data
      setOtherPartyLoading(true)
      try {
        if (userIsArtist) {
          // Artist view: load user data
          if (!chat.userId) {
            console.error('[ChatDetail] Chat missing userId:', chat)
            return
          }
          const userData = await fetchUserById(chat.userId)
          setChatUser(userData)
          console.log('[ChatDetail] Loaded user for artist view')
        } else {
          // User view: load artist data
          if (!chat.artistId) {
            console.error('[ChatDetail] Chat missing artistId:', chat)
            return
          }
          const artistData = await fetchArtistById(chat.artistId)
          setArtist(artistData)
          console.log('[ChatDetail] Loaded artist for user view:', artistData?.displayName || 'Unknown')

          const sourceFromUrl = searchParams.get('source')?.trim()
          if (sourceFromUrl && effectiveChatId) {
            await trackChatOpened(effectiveChatId, chat.artistId, sourceFromUrl)
          }
        }
        
        otherPartyLoadedRef.current = true
      } catch (error) {
        console.error('[ChatDetail] Error loading other party:', error)
      } finally {
        setOtherPartyLoading(false)
      }
    }

    loadOtherPartyData()
  }, [chat, effectiveChatId, isNewChat, artistId, user, userData, searchParams, cachedDisplayData])


  // Effect 6: Handle match flow - create chat with initial messages
  useEffect(() => {
    const createChatWithInitialMessages = async () => {
      if (!isNewChat || isNewChatCreated || initialMessagesSentRef.current) return
      
      // Only handle artist match flow - auto-post idea and inspo
      if (matchIdParam && artistId && user) {
        try {
          console.log('[MatchFlow] Starting match flow for matchId:', matchIdParam)
          
          // Fetch match data to get ideaText and inspoImageUrls
          const matchDocSnap = await getDoc(fsDoc(db, 'matches', matchIdParam))
          if (!matchDocSnap.exists()) return
          
          const matchData = matchDocSnap.data() as any
          const ideaText: string | undefined = matchData.ideaText
          const inspoImageUrls: string[] | undefined = matchData.inspoImageUrls

          // Create the chat (or get existing one)
          const newChatId = await createSimpleChat(artistId, flashId || undefined)
          console.log('[MatchFlow] Chat created:', newChatId)
          
          // Update match document with chatId
          updateMatch(matchIdParam, { chatId: newChatId }).catch(console.error)

          // Check if this is an existing chat
          const existingChat = await fetchChatById(newChatId)
          const hasExistingMessages = existingChat && existingChat.messages.length > 0

          // Only add initial messages if this is truly a new chat
          if (!hasExistingMessages) {
            console.log('[MatchFlow] Adding initial messages to chat')
            
            // Add messages to Firestore - the listener will pick them up
            if (ideaText && ideaText.trim() && Array.isArray(inspoImageUrls) && inspoImageUrls.length > 0) {
              await addCombinedMessageToChat(newChatId, ideaText.trim(), inspoImageUrls, user.uid, 'user')
            } else if (ideaText && ideaText.trim()) {
              await addMessageToChat(newChatId, ideaText.trim(), user.uid, 'user')
            } else if (Array.isArray(inspoImageUrls) && inspoImageUrls.length > 0) {
              for (const imgUrl of inspoImageUrls) {
                await addImageUrlMessageToChat(newChatId, imgUrl, user.uid, 'user', '')
              }
            }

          } else {
            console.log('[MatchFlow] Existing chat with messages, skipping initial messages')
          }

          // Mark other party as already loaded to prevent Effect 4 from re-fetching
          otherPartyLoadedRef.current = true
          
          // Set created chat ID to trigger silent URL update
          // This transitions /chats/new → /chats/{id} in browser history without React Router re-render
          setCreatedChatId(newChatId)
          setIsNewChatCreated(true)
          initialMessagesSentRef.current = true
        } catch (err) {
          console.error('[MatchFlow] Error:', err)
        }
      }
    }

    createChatWithInitialMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewChat, isNewChatCreated, matchIdParam, artistId, user])

  // DISABLED: Old auto-preload flash as a pending blob row (handled via attachedArtistPortfolioItems + hydrate effect)
  // useEffect(() => {
  //   const preloadFlashImage = async () => {
  //     if (!isNewChat || pendingImages.length > 0 || !flash) return

  //     // Create a pending image from flash image URL (already uploaded, so status is 'sent')
  //     const flashPendingImage: PendingImage = {
  //       clientId: `flash-${flash.id}`,
  //       file: new File([], 'flash.jpg', { type: 'image/jpeg' }), // Dummy file, not used for already uploaded images
  //       localPreviewUrl: flash.imageUrl, // Use the actual URL for preview
  //       status: 'sent', // Already uploaded
  //       progress: 100,
  //       permanentUrl: flash.imageUrl
  //     }
  //     setPendingImages([flashPendingImage])
  //   }

  //   preloadFlashImage()
  // }, [isNewChat, pendingImages.length, flash])

  // Event handlers
  // Effect: Handle typing indicator when message changes
  useEffect(() => {
    // Don't handle typing if no effective chat ID
    if (!effectiveChatId || !user) return
    
    // Clear existing timeout first
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }
    
    // If message has content, set typing indicator
    if (message.trim()) {
      setTypingIndicator(effectiveChatId, isArtist).catch(err => 
        console.error('[Typing] Error setting indicator:', err)
      )
      
      // Clear typing indicator after 3 seconds of no activity
      typingTimeoutRef.current = setTimeout(async () => {
        await clearTypingIndicator(effectiveChatId, isArtist).catch(err =>
          console.error('[Typing] Error clearing indicator:', err)
        )
      }, 3000)
    } else {
      // Message is empty, clear typing indicator immediately
      clearTypingIndicator(effectiveChatId, isArtist).catch(err =>
        console.error('[Typing] Error clearing indicator:', err)
      )
    }
    
    // Only cleanup on unmount - don't call it on every dependency change
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [message, effectiveChatId, isArtist, user])

  const handleBackClick = () => {
    if (matcherSessionId) {
      const matcherParams = new URLSearchParams({
        resume: 'results',
        sessionId: matcherSessionId,
        userId: matcherUserId || '',
        selectedCity: matcherSelectedCity,
        intentMode: matcherIntentMode,
      })

      if (matcherIdeaText.trim()) {
        matcherParams.set('ideaText', matcherIdeaText)
      }

      navigate(`/tattoo-artist-matcher?${matcherParams.toString()}`)
      return
    }

    // Try to go back one history location
    // If there's no previous location, fall back to profile
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/profile')
    }
  }

  // Helper to generate optimistic message ID (matches chatData.ts format)
  const generateOptimisticMessageId = () => 'msg_' + Math.random().toString(36).substring(2, 15)
  
  // Helper to generate client-side message ID for tracking
  const generateClientMessageId = () => 'client_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9)

  // Helper to create an optimistic message object
  const createOptimisticMessage = (
    content: string,
    senderId: string,
    senderType: SenderType,
    type: 'text' | 'image' = 'text',
    imageUrl?: string,
    clientGeneratedId?: string,
    replyTo?: MessageReplyTo
  ): Message => ({
    id: generateOptimisticMessageId(),
    content,
    senderId,
    senderType,
    timestamp: new Date(),
    type,
    isRead: false,
    status: 'sending', // PHASE 1 FIX: Clear initial status
    clientGeneratedId: clientGeneratedId || generateClientMessageId(),
    sendAttempts: 0,
    ...(imageUrl && { imageUrl }),
    ...(replyTo && { replyTo }),
  })

  // Apply optimistic update to chat state
  const applyOptimisticMessages = (messages: Message[]) => {
    // Track these message IDs as optimistic (pending sync)
    messages.forEach(m => optimisticMessageIdsRef.current.add(m.id))
    
    setChat(prevChat => {
      if (!prevChat) return prevChat
      
      const lastMessage = messages[messages.length - 1]
      const lastMessagePreview = lastMessage.type === 'image' ? '📷 Image' : lastMessage.content
      
      return {
        ...prevChat,
        messages: [...prevChat.messages, ...messages],
        lastMessage: lastMessagePreview,
        lastMessageAt: new Date()
      }
    })
  }

  const handleSendMessage = async () => {
    console.log('[SendMessage] ========== handleSendMessage called ==========')
    console.log('[SendMessage] Initial state:', {
      hasMessage: !!message.trim(),
      messageLength: message.length,
      hasPendingImages: pendingImages.length > 0,
      attachedArtistPortfolioCount: attachedArtistPortfolioItems.length,
      hasUser: !!user,
      userId: user?.uid,
      isArtist,
      hasArtist: !!artist,
      hasChatUser: !!chatUser,
      isSending,
      // PHASE 1: Enhanced logging
      optimisticMessageIds: Array.from(optimisticMessageIdsRef.current),
      chatMessagesCount: chat?.messages.length || 0,
      pendingImagesDetail: pendingImages.map(img => ({
        clientId: img.clientId,
        status: img.status,
        hasFile: !!img.file,
        hasPermanentUrl: !!img.permanentUrl,
        isFlash: img.isFlash,
        flashId: img.flashId
      }))
    })
    
    // CHECK: Ensure all user-selected images have been uploaded and have permanent URLs
    const readyImages = pendingImages.filter(img => img.permanentUrl)
    const uploadingImages = pendingImages.filter(img => img.status === 'uploading')
    const errorImages = pendingImages.filter(img => img.status === 'error')
    
    if (uploadingImages.length > 0) {
      console.log('[SendMessage] Images still uploading, cannot send yet')
      return
    }
    
    if (errorImages.length > 0) {
      console.log('[SendMessage] Some images failed to upload, user must retry or remove them')
      return
    }

    const portfolioAttachmentsSnapshotForThisSend = [...attachedArtistPortfolioItems]
    
    // Convert artist content to pending images if provided
    let artistContentImages: PendingImage[] = []
    if (portfolioAttachmentsSnapshotForThisSend.length > 0) {
      console.log('[SendMessage] Converting', portfolioAttachmentsSnapshotForThisSend.length, 'artist content items to pending images')
      artistContentImages = portfolioAttachmentsSnapshotForThisSend.map(item => {
        console.log('[SendMessage] Processing item:', {
          id: item.id,
          isFlash: item.isFlash,
          eventId: item.eventId,
          eventExclusive: item.eventExclusive,
          minPrice: item.minPrice,
          minSize: item.minSize
        })
        return {
          clientId: `portfolio-${item.id}`,
          permanentUrl: item.imageUrl,
          localPreviewUrl: item.compressedImageUrl || item.imageUrl,
          status: 'sent' as const,
          progress: 100,
          // Portfolio metadata
          flashId: item.isFlash ? item.id : undefined,
          minPrice: item.minPrice,
          minSize: item.minSize,
          isFlash: item.isFlash ?? false,
          eventId: item.eventId,
          eventExclusive: item.eventExclusive
        }
      })
    }
    
    // Validate we have content and user
    const hasImages = readyImages.length > 0 || artistContentImages.length > 0
    if ((!message.trim() && !hasImages) || !user) {
      console.log('[SendMessage] Early return - no content or no user')
      return
    }
    
    // Prevent duplicate sends
    if (isSending) {
      console.log('[SendMessage] Already sending, ignoring duplicate send attempt')
      return
    }
    
    // Check if we have the required other party data
    // Note: For existing chats, we don't actually need the other party's data loaded to send messages
    // We only need the chat ID and our own user ID. The chatUser/artist data is only for display.
    // Only block if this is a NEW chat and we don't have the other party data yet.
    if (isNewChat) {
      if (isArtist && !chatUser) {
        console.log('[SendMessage] Early return - new chat, artist missing chatUser data')
        return
      }
      if (!isArtist && !artist) {
        console.log('[SendMessage] Early return - new chat, user missing artist data', {
          artist,
          otherPartyLoading,
          chat: chat?.id
        })
        return
      }
    }

    // Allow artists to send messages regardless of lead status

    // Capture message content before clearing (for optimistic update)
    const messageToBeSent = message.trim()
    const replySnapshotForThisSend = messageReplyDraft
    // Combine pending images with artist content images (shared combined cap)
    const allImages = [...pendingImages, ...artistContentImages]
    const imagesToSend = allImages.slice(0, CHAT_COMBINED_ATTACHMENT_LIMIT)
    const senderType: SenderType = isArtist ? 'artist' : 'user'
    const isExistingChat = !isNewChat && effectiveChatId && effectiveChatId !== 'new'
    const messageLength = messageToBeSent.length
    const imageCount = imagesToSend.length
    const messageType: 'text' | 'image' | 'mixed' =
      imageCount > 0 && messageLength > 0
        ? 'mixed'
        : imageCount > 0
        ? 'image'
        : 'text'
    const isFirstMessage = !chat || (chat.messages?.length ?? 0) === 0

    const trackChatSendEvents = async (chatIdForEvent: string, artistIdForEvent?: string) => {
      await trackEvent('chat_message_sent', {
        chat_id: chatIdForEvent,
        role: senderType,
        message_type: messageType,
        length: messageLength,
        image_count: imageCount,
        is_first: isFirstMessage,
        artist_id: artistIdForEvent
      })

      if (isFirstMessage && senderType === 'user') {
        await sendMetaStandardEvent('Lead', {
          content_name: 'artist_chat',
          content_category: 'tattoo_inquiry',
          artist_id: artistIdForEvent,
        })
      }

      if (imageCount > 0) {
        await trackEvent('chat_image_uploaded', {
          chat_id: chatIdForEvent,
          role: senderType,
          count: imageCount,
          artist_id: artistIdForEvent
        })
      }
    }

    // === OPTIMISTIC UPDATE: Apply BEFORE any async work ===
    // For existing chats, show the message instantly (only with permanent URLs)
    const optimisticMessagesWithClientIds: Message[] = []
    
    if (isExistingChat && chat) {
      // BUGFIX: Create base timestamp for this batch of messages
      // Add small offsets to ensure consistent ordering
      const baseTimestamp = new Date()
      let sequenceOffset = 0
      let outboundReplySnapshotAttachedToOptimisticBatch = false

      // Build optimistic messages for images (with PERMANENT URLs ONLY) FIRST
      for (const img of imagesToSend) {
        const clientId = generateClientMessageId()
        // All images must have permanent URLs - they were uploaded before send
        const imageUrl = img.permanentUrl
        if (!imageUrl) {
          console.warn('[SendMessage] Image missing permanent URL, skipping:', img.clientId)
          continue
        }

        const replyForThisOutboundBubble =
          replySnapshotForThisSend && !outboundReplySnapshotAttachedToOptimisticBatch
            ? replySnapshotForThisSend
            : undefined
        if (replyForThisOutboundBubble) {
          outboundReplySnapshotAttachedToOptimisticBatch = true
        }

        const msg = createOptimisticMessage(
          '',
          user.uid,
          senderType,
          'image',
          imageUrl,
          clientId,
          replyForThisOutboundBubble
        )
        // BUGFIX: Add small timestamp offset to preserve order
        msg.timestamp = new Date(baseTimestamp.getTime() + sequenceOffset)
        sequenceOffset += 1 // 1ms offset between messages
        
        // Link message to pending image for status tracking
        msg.pendingImageId = img.clientId
        
        // Attach flash metadata
        if (img.isFlash && img.flashId) {
          msg.metadata = Object.fromEntries(
            Object.entries({
              flashId: img.flashId,
              minPrice: img.minPrice,
              minSize: img.minSize,
              isFlash: true,
              eventId: img.eventId,
              eventExclusive: img.eventExclusive
            }).filter(([_, value]) => value !== undefined)
          )
        }
        
        optimisticMessagesWithClientIds.push(msg)
      }
      
      // Build optimistic message for text AFTER images
      if (messageToBeSent) {
        const clientId = generateClientMessageId()
        const replyForTextBubble =
          replySnapshotForThisSend && !outboundReplySnapshotAttachedToOptimisticBatch
            ? replySnapshotForThisSend
            : undefined
        if (replyForTextBubble) {
          outboundReplySnapshotAttachedToOptimisticBatch = true
        }
        const msg = createOptimisticMessage(
          messageToBeSent,
          user.uid,
          senderType,
          'text',
          undefined,
          clientId,
          replyForTextBubble
        )
        // BUGFIX: Add timestamp offset to ensure text comes after images
        msg.timestamp = new Date(baseTimestamp.getTime() + sequenceOffset)
        optimisticMessagesWithClientIds.push(msg)
      }
      
      // Apply optimistic update SYNCHRONOUSLY before any await
      if (optimisticMessagesWithClientIds.length > 0) {
        console.log('[SendMessage] Applying optimistic update with', optimisticMessagesWithClientIds.length, 'messages')
        applyOptimisticMessages(optimisticMessagesWithClientIds)
      }
    }

    // Clear message text and pending images immediately for clean UI
    // The images are now in the optimistic messages in the chat
    setMessageReplyDraft(null)
    setMessage('')
    setPendingImages([])
    setAttachedArtistPortfolioItems([])

    setIsSending(true)
    try {
      let finalChatId = effectiveChatId

      // If this is a new chat, create it first (only users can create new chats)
      if (isNewChat && !isNewChatCreated && !isArtist) {
        console.log('[SendMessage] Creating new chat')
        const newChatId = await createSimpleChat(artistId!, flashId || undefined)
        finalChatId = newChatId
        
        // === BUILD LOCAL CHAT IMMEDIATELY FOR SEAMLESS UX ===
        const now = new Date()
        const localMessages: Message[] = []
        
        // Note: Do NOT add welcome message to local messages - it will be added by addWelcomeMessageToChat()
        // This avoids duplication and keeps the welcome message in sync with Firestore
        
        // Generate client IDs for all messages upfront
        const imageClientIds = imagesToSend.map(() => generateClientMessageId())
        const textClientId = messageToBeSent ? generateClientMessageId() : null
        
        // BUGFIX: Create base timestamp for this batch with offsets to ensure ordering
        let sequenceOffset = 0
        let newChatOutboundReplySnapshotAttached = false

        // Add user's images with client IDs (use PERMANENT URLs ONLY - already uploaded) FIRST
        imagesToSend.forEach((img, index) => {
          // All images must have permanent URLs at this point
          const imageUrl = img.permanentUrl
          if (!imageUrl) {
            console.warn('[SendMessage] Image missing permanent URL in new chat, skipping:', img.clientId)
            return
          }

          const replyForThisOutboundBubble =
            replySnapshotForThisSend && !newChatOutboundReplySnapshotAttached
              ? replySnapshotForThisSend
              : undefined
          if (replyForThisOutboundBubble) {
            newChatOutboundReplySnapshotAttached = true
          }

          const msg = createOptimisticMessage(
            '',
            user.uid,
            'user',
            'image',
            imageUrl,
            imageClientIds[index],
            replyForThisOutboundBubble
          )
          // BUGFIX: Add timestamp offset to preserve order
          msg.timestamp = new Date(now.getTime() + sequenceOffset)
          sequenceOffset += 1 // 1ms offset between messages
          
          msg.pendingImageId = img.clientId
          
          // Attach flash metadata if this is a flash
          if (img.isFlash && img.flashId) {
            msg.metadata = Object.fromEntries(
              Object.entries({
                flashId: img.flashId,
                minPrice: img.minPrice,
                minSize: img.minSize,
                isFlash: true,
                eventId: img.eventId,
                eventExclusive: img.eventExclusive
              }).filter(([_, value]) => value !== undefined)
            )
          }
          
          localMessages.push(msg)
        })
        
        // Add user's text message with client ID AFTER images
        if (messageToBeSent && textClientId) {
          const replyForTextBubble =
            replySnapshotForThisSend && !newChatOutboundReplySnapshotAttached
              ? replySnapshotForThisSend
              : undefined
          if (replyForTextBubble) {
            newChatOutboundReplySnapshotAttached = true
          }
          const textMsg = createOptimisticMessage(
            messageToBeSent,
            user.uid,
            'user',
            'text',
            undefined,
            textClientId,
            replyForTextBubble
          )
          // BUGFIX: Add timestamp offset to ensure text comes after images
          textMsg.timestamp = new Date(now.getTime() + sequenceOffset)
          localMessages.push(textMsg)
        }
        
        // Build full chat object
        const localChat: Chat = {
          id: newChatId,
          artistId: artistId!,
          userId: user.uid,
          flashId: flashId || null,
          messages: localMessages,
          lastMessage: messageToBeSent || '📷 Image',
          lastMessageAt: now,
          createdAt: now,
          status: 'active',
        }
        
        // Track optimistic message IDs for merging with Firestore updates
        localMessages.forEach(m => optimisticMessageIdsRef.current.add(m.id))
        
        // PHASE 1 FIX: Queue all messages immediately for persistence
        console.log('[SendMessage] 📝 Queueing', localMessages.length, 'new chat messages immediately')
        for (const msg of localMessages) {
          // Queue text and image messages (skip special message types)
          if (msg.type === 'text' || msg.type === 'image') {
            const pendingMsg = createPendingMessage(
              msg.clientGeneratedId!,
              newChatId,
              msg.content,
              msg.senderId,
              msg.senderType,
              msg.type,
              msg.imageUrl, // Will be blob URL for images initially, updated after upload
              msg.metadata,
              msg.replyTo
            )
            await messageQueue.enqueue(pendingMsg).catch(err =>
              console.error('[SendMessage] Failed to enqueue new chat message:', err)
            )
          }
        }
        console.log('[SendMessage] ✅ All new chat messages queued')
        
        // Mark other party as already loaded (we already have artist data from new chat initialization)
        // This prevents Effect 4 from re-fetching and setting otherPartyLoading=true
        otherPartyLoadedRef.current = true
        
        // Set state immediately - NO loading, NO navigation, just instant display
        setChat(localChat)
        setCreatedChatId(newChatId)  // This triggers silent URL update to /chats/{id}
        setIsNewChatCreated(true)
        
        console.log('[SendMessage] Local chat built, syncing to Firestore in background')
        
        // === BACKGROUND SYNC TO FIRESTORE ===
        // Prepare user messages to add to chat (images and text) with client IDs
        const userMessagesToAdd: Array<{
          content: string
          senderId: string
          senderType: SenderType
          isImageUrl?: boolean
          clientGeneratedId?: string
          metadata?: {
            flashId?: string
            minPrice?: string
            minSize?: string
            isFlash?: boolean
            eventId?: string
            eventExclusive?: boolean
          }
          replyTo?: MessageReplyTo
        }> = []
        
        // Build user messages array - images FIRST, then text
        // Add images first (already have permanent URLs from upload phase)
        console.log('[SendMessage] 🆕 NEW CHAT - adding', imagesToSend.length, 'images (already uploaded)')
        let firstNewChatFirestoreReplyAttached = false
        for (let index = 0; index < imagesToSend.length; index++) {
          const img = imagesToSend[index]
          const imageUrl = img.permanentUrl
          
          if (!imageUrl) {
            console.warn('[SendMessage] Image missing permanent URL, skipping:', img.clientId)
            continue
          }
          
          const messageMetadata = img.isFlash && img.flashId ? {
            flashId: img.flashId,
            minPrice: img.minPrice,
            minSize: img.minSize,
            isFlash: true,
            eventId: img.eventId,
            eventExclusive: img.eventExclusive
          } : undefined
          
          console.log('[SendMessage] Creating message with metadata:', {
            hasFlash: img.isFlash,
            eventId: img.eventId,
            eventExclusive: img.eventExclusive,
            metadata: messageMetadata
          })
          
          const replyForThisUserPayloadRow =
            replySnapshotForThisSend && !firstNewChatFirestoreReplyAttached
              ? replySnapshotForThisSend
              : undefined
          if (replyForThisUserPayloadRow) {
            firstNewChatFirestoreReplyAttached = true
          }

          userMessagesToAdd.push({
            content: imageUrl,
            senderId: user.uid,
            senderType: 'user',
            isImageUrl: true,
            clientGeneratedId: imageClientIds[index],
            metadata: messageMetadata,
            ...(replyForThisUserPayloadRow && { replyTo: replyForThisUserPayloadRow }),
          })
        }
        console.log('[SendMessage] ✅ All new chat images prepared')
        
        // Then add text message
        if (messageToBeSent && textClientId) {
          const replyForTextUserPayloadRow =
            replySnapshotForThisSend && !firstNewChatFirestoreReplyAttached
              ? replySnapshotForThisSend
              : undefined
          if (replyForTextUserPayloadRow) {
            firstNewChatFirestoreReplyAttached = true
          }
          userMessagesToAdd.push({
            content: messageToBeSent,
            senderId: user.uid,
            senderType: 'user',
            isImageUrl: false,
            clientGeneratedId: textClientId,
            ...(replyForTextUserPayloadRow && { replyTo: replyForTextUserPayloadRow }),
          })
        }
        
        const hasWelcomeSettings = artistChatSettings?.mode === 'welcome_message' && artistChatSettings?.welcomeMessage?.content
        const subscriptionIsActive = artistSubscriptionStatus === 'active' || artistSubscriptionStatus === 'trial'
        // Only use welcome message if subscription is active
        const shouldUseWelcomeMessage = hasWelcomeSettings && subscriptionIsActive
        const artistUserIdForWelcome = artist?.userId || artistId
        
        console.log('[SendMessage] First message flow:', {
          hasWelcomeSettings,
          subscriptionIsActive,
          shouldUseWelcomeMessage,
          artistUserId: artist?.userId,
          artistId,
          fallbackUserId: artistUserIdForWelcome,
          userMessagesCount: userMessagesToAdd.length
        })
        
        // Add welcome message and user messages atomically for seamless visual transition
        if (shouldUseWelcomeMessage && artistUserIdForWelcome && userMessagesToAdd.length > 0) {
          console.log('[SendMessage] Adding welcome message with first user message atomically', {
            hasWelcome: true,
            userMessagesCount: userMessagesToAdd.length,
            artistUserId: artistUserIdForWelcome
          })
          // Use the new atomic function that adds welcome message and user messages together
          await addWelcomeMessageToChat(
            newChatId,
            artistChatSettings.welcomeMessage?.content || '',
            artistUserIdForWelcome,
            userMessagesToAdd
          )
        } else if (userMessagesToAdd.length > 0) {
          // No welcome message, just add user messages normally
          console.log('[SendMessage] Adding user messages (no welcome message)', {
            userMessagesCount: userMessagesToAdd.length,
            reason: !shouldUseWelcomeMessage ? (subscriptionIsActive ? 'no_welcome_settings' : 'subscription_not_active') : 'no_artist_id'
          })
          for (const msg of userMessagesToAdd) {
            try {
              if (msg.isImageUrl) {
                await addImageUrlMessageToChat(
                  newChatId, 
                  msg.content, 
                  msg.senderId, 
                  msg.senderType, 
                  '',
                  msg.clientGeneratedId,
                  msg.metadata,
                  msg.replyTo
                )
              } else {
                await addMessageToChat(
                  newChatId, 
                  msg.content, 
                  msg.senderId, 
                  msg.senderType,
                  'text',
                  msg.clientGeneratedId,
                  msg.replyTo
                )
              }
              
              // Remove from queue on successful send
              if (msg.clientGeneratedId) {
                await messageQueue.dequeue(msg.clientGeneratedId).catch(err =>
                  console.error('[SendMessage] Failed to dequeue new chat message:', err)
                )
              }
            } catch (error) {
              console.error('[SendMessage] Failed to send message to Firestore:', error)
              // Message remains in queue for retry
            }
          }
        }
        
        // Track message sent
        await trackChatSendEvents(newChatId, artistId!)
        
        // NO navigate() - we stay on the same page with updated state
      } else if (finalChatId && finalChatId !== 'new') {
        // Existing chat - optimistic update already applied above
        console.log('[SendMessage] Syncing message to existing chat:', finalChatId)
        
        // PHASE 1 FIX: QUEUE ALL MESSAGES IMMEDIATELY ===
        // All images already have permanent URLs from upload phase
        console.log('[SendMessage] 📝 Queueing', optimisticMessagesWithClientIds.length, 'messages immediately')
        for (const msg of optimisticMessagesWithClientIds) {
          // Only queue text and image messages (skip notification/booking messages)
          if (msg.type === 'text' || msg.type === 'image') {
            const pendingMsg = createPendingMessage(
              msg.clientGeneratedId!,
              finalChatId,
              msg.content,
              msg.senderId,
              msg.senderType,
              msg.type,
              msg.imageUrl, // Now only permanent URLs - no blob URLs
              msg.metadata,
              msg.replyTo
            )
            await messageQueue.enqueue(pendingMsg).catch(err =>
              console.error('[SendMessage] Failed to enqueue message:', err)
            )
          }
        }
        console.log('[SendMessage] ✅ All messages queued')
        
        // === BACKGROUND SYNC: Send messages to Firestore ===
        const chatIdForSync = finalChatId
        const artistIdForSync = chat!.artistId
        const messagesToSync = [...optimisticMessagesWithClientIds]
        
        ;(async () => {
          try {
            console.log('[SendMessage] 🚀 BACKGROUND SYNC START - sending', messagesToSync.length, 'messages')
            
            // Send each message to Firestore with its clientGeneratedId
            // All images already have permanent URLs - no upload needed
            console.log('[SendMessage] 📤 About to send', messagesToSync.length, 'messages to Firestore')
            for (const msg of messagesToSync) {
              console.log('[SendMessage] Sending message to Firestore:', {
                type: msg.type,
                clientGeneratedId: msg.clientGeneratedId,
                imageUrl: msg.imageUrl?.substring(0, 60)
              })
              
              if (msg.type === 'image') {
                console.log('[SendMessage] 🖼️ Sending image message with URL:', msg.imageUrl?.substring(0, 100))
                await addImageUrlMessageToChat(
                  chatIdForSync, 
                  msg.imageUrl!, 
                  user.uid, 
                  senderType, 
                  msg.content,
                  msg.clientGeneratedId,
                  msg.metadata,
                  msg.replyTo
                )
              } else if (msg.type === 'text') {
                await addMessageToChat(
                  chatIdForSync, 
                  msg.content, 
                  user.uid, 
                  senderType,
                  'text',
                  msg.clientGeneratedId,
                  msg.replyTo
                )
              }
              
              // Remove from queue on successful send
              if (msg.clientGeneratedId) {
                await messageQueue.dequeue(msg.clientGeneratedId).catch(err =>
                  console.error('[SendMessage] Failed to dequeue message:', err)
                )
              }
            }

            // Track message sent
            await trackChatSendEvents(chatIdForSync, artistIdForSync)
          } catch (error) {
            console.error('[SendMessage] Background sync error:', error)
            
            // Messages will remain in queue and be retried automatically
            // Update message status in chat state to show they failed
            for (const msg of messagesToSync) {
              if (msg.clientGeneratedId) {
                // Update the pending message in queue with error
                const pendingMsg = await messageQueue.get(msg.clientGeneratedId)
                if (pendingMsg) {
                  const updatedMsg = markForRetry(pendingMsg, String(error))
                  await messageQueue.update(updatedMsg).catch(err =>
                    console.error('[SendMessage] Failed to update message in queue:', err)
                  )
                }
              }
            }
          }
        })()
      }

      // Mark that user has made a decision on artist contact if sending a second message
      // (continuing to chat with assistant instead of requesting artist)
      if (!isArtist && finalChatId && chat && chat.messages && chat.messages.length > 2) {
        // If there are already messages in the chat (more than 1), this is at least a second message
        setUserMadeDecisionOnArtistContact(true)
      }

    } catch (error) {
      console.error('[SendMessage] Error:', error)
      // Restore input state on error so user doesn't lose their message
      setMessage(messageToBeSent)
      setMessageReplyDraft(replySnapshotForThisSend)
      setAttachedArtistPortfolioItems(portfolioAttachmentsSnapshotForThisSend)
    } finally {
      setIsSending(false)
      
      // Clear typing indicator after sending
      if (effectiveChatId) {
        clearTypingIndicator(effectiveChatId, isArtist).catch(err =>
          console.error('[Typing] Error clearing indicator after send:', err)
        )
      }
    }
  }

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) {
      console.warn('[ImageSelect] No user authenticated')
      return
    }

    try {
      const files = Array.from(event.target.files || []).filter(f => f.type.startsWith('image/'))
      
      // Log file selection for debugging
      console.log('[ImageSelect] Selected', files.length, 'image files')
      
      if (files.length === 0) {
        console.warn('[ImageSelect] No image files selected or user cancelled')
        return
      }
      
      // Accept files up to 50MB - we'll compress them if needed
      // Most smartphones now take 12MP+ photos which can be 15-40MB
      const maxFileSize = 50 * 1024 * 1024
      const oversizedFiles: Array<{ name: string; size: string }> = []
      const validFiles = files.filter(f => {
        if (f.size > maxFileSize) {
          const sizeInMB = (f.size / 1024 / 1024).toFixed(1)
          console.warn(`[ImageSelect] File ${f.name} is too large (${sizeInMB}MB)`)
          oversizedFiles.push({ name: f.name, size: sizeInMB })
          return false
        }
        return true
      })
      
      if (validFiles.length === 0) {
        console.error('[ImageSelect] All files exceeded size limit')
        const oversizedNames = oversizedFiles.map(f => `${f.name} (${f.size}MB)`).join(', ')
        throw new Error(`File size exceeds 50MB limit. Files too large: ${oversizedNames}. Please select smaller images.`)
      }
      
      // If some files were oversized but not all, warn about rejected files
      if (oversizedFiles.length > 0) {
        const rejectedNames = oversizedFiles.map(f => `${f.name} (${f.size}MB)`).join(', ')
        console.warn(`[ImageSelect] Some files were too large and skipped: ${rejectedNames}`)
      }
      
      const currentUploadedImageCount = pendingImages.length + attachedArtistPortfolioItems.length
      const availableSlots = Math.max(0, CHAT_COMBINED_ATTACHMENT_LIMIT - currentUploadedImageCount)
      if (availableSlots <= 0) {
        console.warn('[ImageSelect] Already have maximum images')
        return
      }
      
      const filesToAdd = validFiles.slice(0, availableSlots)
      console.log('[ImageSelect] Creating shimmer preview for', filesToAdd.length, 'images')
      
      // Create pending images with shimmer loading state - NO BLOB URLs
      // Display with localPreviewUrl from file immediately, then upload
      const newPendingImages: PendingImage[] = filesToAdd.map(file => ({
        clientId: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        localPreviewUrl: URL.createObjectURL(file), // Temporary blob URL for shimmer preview only
        status: 'uploading' as const, // Start in uploading state immediately
        progress: 10, // Show progress started
        uploadAttempts: 0
      }))
      
      // Add to state immediately for instant shimmer preview
      setPendingImages(prev => [...prev, ...newPendingImages])
      
      console.log('[ImageSelect] Shimmer previews created, starting uploads...')
      
      // Upload all images immediately in background - DON'T wait for send
      ;(async () => {
        try {
          const { uploadImageToStorage } = await import('@/lib/firebase')
          
          for (const img of newPendingImages) {
            try {
              console.log('[ImageSelect] Uploading image:', img.clientId)
              
              // Update progress
              setPendingImages(prev => prev.map(p => 
                p.clientId === img.clientId ? { ...p, progress: 30 } : p
              ))
              
              // Upload to Firebase Storage
              const permanentUrl = await uploadImageToStorage(img.file!, 'chat-images', user.uid)
              
              console.log('[ImageSelect] ✅ Upload complete:', img.clientId)
              
              // Update with permanent URL and mark as sent
              setPendingImages(prev => prev.map(p => 
                p.clientId === img.clientId 
                  ? { ...p, status: 'sent' as const, progress: 100, permanentUrl } 
                  : p
              ))
            } catch (error) {
              const errorMsg = error instanceof Error ? error.message : String(error)
              console.error('[ImageSelect] Upload failed for:', img.clientId, {
                error: errorMsg,
                errorType: error instanceof Error ? error.name : typeof error,
                fullError: error
              })
              // Mark as error, user can retry or remove
              setPendingImages(prev => prev.map(p => 
                p.clientId === img.clientId 
                  ? { ...p, status: 'error' as const, error: errorMsg, uploadAttempts: (p.uploadAttempts || 0) + 1 } 
                  : p
              ))
            }
          }
        } catch (error) {
          console.error('[ImageSelect] Import error:', error)
        }
      })()
      
    } catch (error) {
      console.error('[ImageSelect] Error:', error)
    }
  }

  const handleRemoveImage = (clientId: string) => {
    setPendingImages(prev => {
      // Revoke blob URL to free memory
      const imgToRemove = prev.find(p => p.clientId === clientId)
      if (imgToRemove && imgToRemove.localPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imgToRemove.localPreviewUrl)
      }
      return prev.filter(p => p.clientId !== clientId)
    })
  }

  const handleRetryImageUpload = async (clientId: string) => {
    const pendingImg = pendingImages.find(p => p.clientId === clientId)
    if (!pendingImg || !user) {
      console.warn('[RetryImageUpload] Image not found or no user:', clientId)
      return
    }

    // Don't retry flash images - they already have permanent URLs
    if (pendingImg.flashId || !pendingImg.file) {
      console.warn('[RetryImageUpload] Skipping retry for flash image:', clientId)
      return
    }

    try {
      console.log('[RetryImageUpload] Retrying upload for:', clientId)
      
      // Update status to uploading
      setPendingImages(prev => prev.map(p => 
        p.clientId === clientId ? { ...p, status: 'uploading' as const, progress: 0, error: undefined } : p
      ))
      
      const { uploadImageToStorage } = await import('@/lib/firebase')
      const permanentUrl = await uploadImageToStorage(pendingImg.file, 'chat-images', user.uid)
      
      // Update with permanent URL and mark as sent
      setPendingImages(prev => prev.map(p => 
        p.clientId === clientId 
          ? { ...p, status: 'sent' as const, progress: 100, permanentUrl } 
          : p
      ))
      
      console.log('[RetryImageUpload] Upload successful')
    } catch (error) {
      console.error('[RetryImageUpload] Upload failed:', error)
      // Mark as error again
      setPendingImages(prev => prev.map(p => 
        p.clientId === clientId 
          ? { ...p, status: 'error' as const, error: String(error), uploadAttempts: (p.uploadAttempts || 0) + 1 } 
          : p
      ))
    }
  }

  /**
   * Manually retry a failed message
   */
  const handleRetryMessage = async (clientGeneratedId: string) => {
    console.log('[RetryMessage] Manual retry requested for:', clientGeneratedId)
    
    try {
      // Get the pending message from queue
      const pendingMsg = await messageQueue.get(clientGeneratedId)
      
      if (!pendingMsg) {
        console.warn('[RetryMessage] Message not found in queue:', clientGeneratedId)
        return
      }
      
      // Update message status to 'sending' in UI
      setChat(prevChat => {
        if (!prevChat) return prevChat
        
        return {
          ...prevChat,
          messages: prevChat.messages.map(m =>
            m.clientGeneratedId === clientGeneratedId
              ? { ...m, status: 'sending' as const }
              : m
          )
        }
      })
      
      // Attempt to retry
      await retryPendingMessage(pendingMsg)
    } catch (error) {
      console.error('[RetryMessage] Error during manual retry:', error)
    }
  }

  const replaceAttachedArtistPortfolioSelection = (itemsFromSelector: PortfolioItem[]) => {
    const maximumSelectablePortfolioSlotsRemaining = Math.max(
      0,
      CHAT_COMBINED_ATTACHMENT_LIMIT - pendingImages.length
    )
    setAttachedArtistPortfolioItems(
      itemsFromSelector.slice(0, maximumSelectablePortfolioSlotsRemaining)
    )
  }

  const removeAttachedArtistPortfolioItemById = (portfolioItemIdentifierToRemove: string) => {
    setAttachedArtistPortfolioItems(previousItems =>
      previousItems.filter(item => item.id !== portfolioItemIdentifierToRemove)
    )
  }

  return {
    // State
    artist,
    chatUser,
    otherPartyLoading,
    chat,
    chatLoading,
    message,
    setMessage,
    isNewChatCreated,
    pendingImages,
    attachedArtistPortfolioItems,
    replaceAttachedArtistPortfolioSelection,
    removeAttachedArtistPortfolioItemById,
    isArtist,
    userMadeDecisionOnArtistContact,
    setUserMadeDecisionOnArtistContact,
    artistChatSettings,
    artistSubscriptionStatus,
    otherPartyIsTyping,
    isSending,
    messageReplyDraft,
    
    // Computed values
    isNewChat,
    effectiveChatId,
    
    // Helper functions
    getOtherPartyDisplayName,
    getOtherPartySubtitle,
    getReplyAuthorDisplayLabel,
    beginReplyToMessage,
    beginReplyToImageBurst,
    clearMessageReplyDraft,
    
    // Event handlers
    handleBackClick,
    handleSendMessage,
    handleImageSelect,
    handleRemoveImage,
    handleRetryMessage,
    handleRetryImageUpload,
    
    // Other props
    flash,
  }
}
